import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ICONS, REPORTS, AS_OF, type ReportCol, type ReportId, type ReportSection, presetRange, reportQuery, useReport, useReportInfo, ymd } from "../../hooks/reports";
import { useLocale } from "../../hooks/useLocale";
import { money } from "../../lib/format";
import { Empty } from "../ui/Badges";
import { DownloadButton } from "../ui/DownloadButton";
import { PrintButton } from "../ui/PrintButton";

const PRESETS = ["today", "yesterday", "week", "month", "lastMonth", "quarter", "year"] as const;
const fmt = (c: ReportCol, v: string | number | null | undefined) => {
  if (v === null || v === undefined || v === "") return "—";
  if (c.kind === "money") return money(Number(v));
  if (c.kind === "int") return Number(v).toLocaleString("en-US", { maximumFractionDigits: 3 });
  if (c.kind === "percent") return `${v}%`;
  return String(v);
};

/** Bars for a section that has a chart column (days, hours, weekdays, cashiers). */
function Bars({ s }: { s: ReportSection }) {
  const c = s.chart!;
  const col = s.columns.find((x) => x.key === c.value)!;
  const max = Math.max(1, ...s.rows.map((r) => Number(r[c.value]) || 0));
  if (s.rows.length === 0) return null;
  return (
    <div className="space-y-1" role="img" aria-label={s.title} data-chart={s.key}>
      {s.rows.slice(0, 40).map((r, i) => {
        const v = Number(r[c.value]) || 0;
        return (
          <div key={i} className="flex items-center gap-2 text-[10px] font-bold text-gray-600">
            <span className="num w-20 flex-shrink-0 truncate font-mono" dir="ltr">{String(r[c.label])}</span>
            <div className="h-3 flex-1 overflow-hidden rounded bg-gray-100"><div className="h-full rounded bg-cosmic-purple" style={{ width: `${(v / max) * 100}%` }} /></div>
            <span className="num w-20 flex-shrink-0 text-end font-mono">{fmt(col, v)}</span>
          </div>
        );
      })}
    </div>
  );
}

function SectionTable({ s, totalLabel }: { s: ReportSection; totalLabel: string }) {
  const totals = s.totals && s.rows.length > 0
    ? Object.fromEntries(s.columns.filter((c) => c.kind === "money" || c.kind === "int").map((c) => [c.key, s.rows.reduce((sum, r) => sum + (Number(r[c.key]) || 0), 0)]))
    : null;
  return (
    <section className="card space-y-2 p-4" aria-label={s.title} data-section={s.key}>
      <h2 className="text-sm font-black text-cosmic-navy">{s.title}</h2>
      {s.note && <p className="text-[11px] font-semibold text-gray-500">{s.note}</p>}
      {s.chart && <Bars s={s} />}
      <div className="overflow-x-auto">
        <table className="w-full text-start text-xs">
          <thead className="border-b border-gray-100 bg-gray-50 text-[10px] font-black uppercase tracking-wider text-gray-500">
            <tr>{s.columns.map((c) => <th key={c.key} className={`px-3 py-2 ${c.kind === "text" ? "text-start" : "text-end"}`}>{c.header}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-gray-100 font-bold text-gray-700">
            {s.rows.length === 0 ? <tr><td colSpan={s.columns.length} className="px-3 py-4 text-center text-gray-400">—</td></tr> : s.rows.map((r, i) => (
              <tr key={i}>{s.columns.map((c) => <td key={c.key} className={`px-3 py-2 ${c.kind === "text" ? "" : "num text-end font-mono"}`}>{fmt(c, r[c.key])}</td>)}</tr>
            ))}
            {totals && (
              <tr className="bg-purple-50/60 font-black text-cosmic-navy">
                <td className="px-3 py-2">{totalLabel}</td>
                {s.columns.slice(1).map((c) => <td key={c.key} className="num px-3 py-2 text-end font-mono">{c.key in totals ? fmt(c, totals[c.key]) : ""}</td>)}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** Reports: sales, tax, shifts, margin, receivables aging, wallet liability, staff, visits, inventory and the day book, for any period. */
export function ReportsPanel() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const info = useReportInfo();
  const [id, setId] = useState<ReportId>("pnl");
  const [range, setRange] = useState(presetRange("month"));
  const [preset, setPreset] = useState<string>("month");
  const [scope, setScope] = useState<"branch" | "all">("branch");
  const q = useReport(id, range.from, range.to, scope, locale);
  const asOf = AS_OF.includes(id);
  const query = reportQuery(range.from, range.to, scope, locale);
  const rep = q.data;
  const tone = (k?: string) => (k === "good" ? "text-emerald-600" : k === "bad" ? "text-red-600" : k === "warn" ? "text-amber-700" : "text-cosmic-navy");

  return (
    <div className="space-y-5">
      <div className="card flex items-center gap-3.5 p-5">
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-tr from-cosmic-purple to-solar-orange text-2xl shadow-cosmic" aria-hidden>📊</div>
        <div><h1 className="text-xl font-black tracking-tight">{t("reports.title")}</h1><p className="text-xs font-medium text-gray-500">{t("reports.subtitle")}</p></div>
      </div>

      <div className="flex flex-wrap gap-1.5" role="tablist" aria-label={t("reports.title")}>
        {REPORTS.map((r) => (
          <button key={r} role="tab" type="button" data-report={r} aria-selected={id === r} onClick={() => setId(r)}
            className={`rounded-xl px-3.5 py-2 text-xs font-black transition ${id === r ? "bg-cosmic-purple text-white shadow-cosmic" : "bg-white text-gray-600 hover:bg-purple-50"}`}>{ICONS[r]} {t(`reports.names.${r}`)}</button>
        ))}
      </div>

      <div className="card space-y-3 p-4">
        <div className="flex flex-wrap items-end gap-3">
          {!asOf && (
            <>
              <div className="flex flex-wrap gap-1 text-[11px] font-black" role="group" aria-label={t("reports.period")}>
                {PRESETS.map((p) => (
                  <button key={p} type="button" aria-pressed={preset === p} onClick={() => { setPreset(p); setRange(presetRange(p)); }} className={`rounded-lg px-2.5 py-1 ${preset === p ? "bg-cosmic-navy text-white" : "bg-gray-100 text-gray-700"}`}>{t(`reports.presets.${p}`)}</button>
                ))}
              </div>
              <div><label className="label" htmlFor="rp-from">{t("reports.from")}</label><input id="rp-from" className="field num !py-1.5" type="date" value={range.from} max={ymd(new Date())} onChange={(e) => { setPreset("custom"); setRange({ ...range, from: e.target.value }); }} /></div>
              <div><label className="label" htmlFor="rp-to">{t("reports.to")}</label><input id="rp-to" className="field num !py-1.5" type="date" value={range.to} max={ymd(new Date())} onChange={(e) => { setPreset("custom"); setRange({ ...range, to: e.target.value }); }} /></div>
            </>
          )}
          {info.data?.canAllBranches && (
            <div><label className="label" htmlFor="rp-scope">{t("reports.scope")}</label>
              <select id="rp-scope" className="field !py-1.5" value={scope} onChange={(e) => setScope(e.target.value as "branch" | "all")}><option value="branch">{t("reports.thisBranch")}</option><option value="all">{t("reports.allBranches")}</option></select></div>
          )}
          <span className="flex-1" />
          <div className="flex flex-wrap gap-2">
            <DownloadButton path={`/reports/${id}/file?${query}&output=pdf`} fallbackName={`${id}.pdf`} className="btn-primary !px-3 !py-1.5 text-xs">📄 PDF (A4)</DownloadButton>
            <DownloadButton path={`/reports/${id}/file?${query}&output=xlsx`} fallbackName={`${id}.xlsx`} className="btn-ghost !px-3 !py-1.5 text-xs">📗 Excel</DownloadButton>
            <DownloadButton path={`/reports/${id}/file?${query}&output=csv`} fallbackName={`${id}.csv`} className="btn-ghost !px-3 !py-1.5 text-xs">CSV</DownloadButton>
            <PrintButton path={`/reports/${id}/file?${query}&output=pdf`} className="btn-dark !px-3 !py-1.5 text-xs" />
          </div>
        </div>
        {asOf && <p className="text-[11px] font-semibold text-gray-500">{t("reports.asOf")}</p>}
        {range.from > range.to && <p className="text-[11px] font-bold text-red-600">{t("reports.badPeriod")}</p>}
      </div>

      {q.isLoading ? <Empty>{t("common.loading")}</Empty> : q.isError || !rep ? <Empty>{t("reports.loadError")}</Empty> : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-5" data-testid="kpis">
            {rep.kpis.map((k) => (
              <div key={k.key} className="card p-3.5" data-kpi={k.key}>
                <div className="text-[10px] font-black uppercase tracking-wide text-gray-500">{k.label}</div>
                <div className={`num mt-1 font-mono text-lg font-black ${tone(k.tone)}`}>
                  {k.kind === "money" ? money(Number(k.value)) : k.kind === "percent" ? `${k.value}%` : k.kind === "int" ? Number(k.value).toLocaleString("en-US") : String(k.value)}
                  {k.kind === "money" && <span className="ms-1 text-[10px] font-bold text-gray-400">{t("currency")}</span>}
                </div>
              </div>
            ))}
          </div>
          <div className="space-y-4">{rep.sections.map((s) => <SectionTable key={s.key} s={s} totalLabel={t("reports.total")} />)}</div>
          {rep.notes.map((n, i) => <p key={i} className="text-[11px] font-semibold text-gray-500">ℹ️ {n}</p>)}
        </>
      )}
    </div>
  );
}
